import { useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// material
import {
  Card,
  Table,
  Stack,
  Button,
  Box,
  Modal,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Backdrop,
  CircularProgress
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

// excel
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';

// graphql
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { RegisterUserGQLFunction } from '../graphql/REGISTER_USER';

// components
import Page from '../components/Page';
import Iconify from '../components/Iconify';
import Label from '../components/Label';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../sections/people/SearchNotFound';
import { ListHead, ListToolbar, LoadingList } from '../sections/people';
import EmptyList from '../sections/people/EmptyList';

// info
import { REGISTER_USER } from '../info/REGISTER_USER';

// public resources
import templateFile from './template.xlsx';

// scss
import './Table.scss';

function TableContent({ data, page, rowsPerPage }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!data) data = [];
  return (
    <TableBody>
      {data.map((user, index) => (
        <TableRow hover key={index} onClick={() => navigate(`/register/info/${user.id}`)}>
          <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
          <TableCell align="left" className="no-wrap">{user.userId}</TableCell>
          <TableCell align="left" className="no-wrap">{user.authenticator}</TableCell>
          <TableCell>
            <Typography variant="subtitle2" noWrap>
              {user.username}
            </Typography>
          </TableCell>
          <TableCell align="left">{user.nickname || '-'}</TableCell>
          <TableCell align={user.title ? 'left' : 'center'}>{user.title || '-'}</TableCell>
          <TableCell align="left">
            <Label variant="ghost" color={'default'}>
              {user.userType !== undefined && REGISTER_USER.USER_TYPE[user.userType]
                ? t(`common.${REGISTER_USER.USER_TYPE[user.userType]}`)
                : t('common.unknown')}
            </Label>
          </TableCell>
          <TableCell align="left">{user.authCode}</TableCell>
          <TableCell align="left">{user.registerDate}</TableCell>
          <TableCell align="left">
            <Button
              variant="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/user/history?userId=${user.userId}&id=${user.id}&from=register`);
              }}
            >
              {t('history.title')}
            </Button>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );
}

function AllUserTable({ page, rowsPerPage, setTotal, reload }) {
  const { loading, data, refetch } = useQuery(RegisterUserGQLFunction.GET_ALL_REGISTER_USERS, {
    variables: { page, rowsPerPage },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    refetch();
  }, [reload, refetch]);

  useEffect(() => {
    if (!loading && data) {
      setTotal(data.allRegisterUsers.len);
    }
  }, [loading, data, setTotal]);

  if (loading) {
    return <LoadingList TABLE_HEAD={REGISTER_USER.TABLE_HEAD} />;
  }

  if (!data?.allRegisterUsers.len) {
    return (
      <TableBody>
        <TableRow>
          <TableCell align="center" colSpan={REGISTER_USER.TABLE_HEAD.length} sx={{ py: 3, fontWeight: 'bold', fontSize: '1.1rem' }}>
            <EmptyList />
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return <TableContent data={data.allRegisterUsers.registerUsers} page={page} rowsPerPage={rowsPerPage} />;
}

function SearchResultTable({ page, rowsPerPage, searchField, searchInput, searchRegistered, setTotal, reload }) {
  const { loading, data, refetch } = useQuery(RegisterUserGQLFunction.GET_SEARCH_REGISTER_USERS, {
    variables: { page, rowsPerPage, searchField, searchStr: searchInput, searchRegistered },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    refetch();
  }, [reload, refetch]);

  useEffect(() => {
    if (!loading && data) {
      setTotal(data.searchRegisterUsers.len);
    }
  }, [loading, data, setTotal]);

  if (loading) {
    return <LoadingList TABLE_HEAD={REGISTER_USER.TABLE_HEAD} />;
  }

  return data && data.searchRegisterUsers.len ? (
    <TableContent data={data.searchRegisterUsers.registerUsers} page={page} rowsPerPage={rowsPerPage} />
  ) : (
    <TableBody>
      <TableRow>
        <TableCell align="center" colSpan={REGISTER_USER.TABLE_HEAD.length} sx={{ py: 3, fontWeight: 'bold', fontSize: '1.1rem' }}>
          <SearchNotFound searchQuery={searchInput} />
        </TableCell>
      </TableRow>
    </TableBody>
  );
}

export default function RegisterUser() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const confirm = useConfirm();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchField, setSearchField] = useState('username');
  const [searchInput, setSearchInput] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [open, setOpen] = useState(false);
  const [reload, setReload] = useState(0);

  const [openModal, setOpenModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [searchRegistered, setSearchRegistered] = useState('all');
  const [createRegisterUsers] = useMutation(RegisterUserGQLFunction.CREATE_REGISTER_USERS);
  const [isEmailRegistered] = useLazyQuery(RegisterUserGQLFunction.IS_EMAIL_REGISTERED);
  const [exportRegisterUsers] = useLazyQuery(RegisterUserGQLFunction.EXPORT_REGISTER_USERS);

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4
  };

  useEffect(() => {
    setPage(0);
  }, [searchField, searchInput, searchRegistered]);

  const handleExport = async () => {
    const { data } = await exportRegisterUsers({
      variables: { searchField, searchRegistered, searchStr: searchInput }
    });

    if (!data 
        || !data.exportRegisterUsers
        || !data.exportRegisterUsers.registerUsers
        || data.exportRegisterUsers.registerUsers.length == 0) {
      setErrorMsg(t('people.no_export_data'));
      setOpenModal(true);
      return true;
    }

    const tableData = data.exportRegisterUsers.registerUsers;

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Sheet1');
    sheet.columns = [
      { header: 'userId', key: 'userId', width: 32, style: { font: { size: 14 } } },
      { header: 'username', key: 'username', width: 32, style: { font: { size: 14 } } },
      { header: 'nickname', key: 'nickname', width: 32, style: { font: { size: 14 } } },
      { header: 'title', key: 'title', width: 48, style: { font: { size: 14 } } },
      { header: 'email', key: 'email', width: 32, style: { font: { size: 14 } } },
      { header: 'authCode', key: 'authCode', width: 32, style: { font: { size: 14 } } },
      { header: 'registerDate', key: 'registerDate', width: 32, style: { font: { size: 14 } } }
    ];

    if (tableData) {
      tableData.forEach((user) => {
        sheet.addRow({
          userId: user.userId,
          username: user.username,
          nickname: user.nickname,
          title: user.title,
          email: user.authenticator,
          authCode: user.authCode,
          registerDate: user.registerDate
        });
      });
    }

    workbook.xlsx.writeBuffer().then((content) => {
      const link = document.createElement('a');
      const blobData = new Blob([content], {
        type: 'application/vnd.ms-excel;charset=utf-8;'
      });
      link.download = 'export_users.xlsx';
      link.href = URL.createObjectURL(blobData);
      link.click();
    });
  };

  const handleTemplate = async () => {
    const link = document.createElement('a');
    link.href = templateFile;
    link.click();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleModalClose = () => {
    setOpenModal((prev) => !prev);
    setErrorMsg('');
  };

  const checkIsEmailRegistered = async (users) => {
    for (let i = 0; i < users.length; i += 1) {
      const { email } = users[i];
      const { data } = await isEmailRegistered({ variables: { authenticator: email } });
      if (data.isEmailRegistered) {
        setErrorMsg(t('people.email_exists_param', { email }));
        setOpenModal(true);
        return true;
      }
    }
    return false;
  };

  const handleUpload = async (e) => {
    if (!e || !e.target || !e.target.files || e.target.files.length == 0) {
      return;
    }

    const file = e.target.files[0];
    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      setOpen(true);
      const bufferArray = e.target.result;
      const wb = XLSX.read(bufferArray, { type: 'buffer' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const upload_users_json = XLSX.utils.sheet_to_json(ws, { defval: '' }).map(row => {
        const processedRow = {};
        Object.keys(row).forEach(key => {
          // Convert every value to a string to ensure UTF8-compatible string data is sent to backend
          processedRow[key] = row[key] !== null && row[key] !== undefined ? String(row[key]).trim() : '';
        });
        return processedRow;
      });

      const err = await checkIsEmailRegistered(upload_users_json);

      if (!err) {
        const args = upload_users_json.map((user) => ({
          userId: user.userId,
          authenticator: user.email,
          username: user.username,
          nickname: user['nickname (optional)'] || '',
          title: user['title (optional)'] || '',
          userType: 0  // person
        }));

        try {
          const { data } = await createRegisterUsers({
            variables: {registerUsers: args},
            refetchQueries: [
              {
                query: RegisterUserGQLFunction.GET_ALL_REGISTER_USERS,
                variables: { page: 0, rowsPerPage }
              },
              {
                query: RegisterUserGQLFunction.GET_SEARCH_REGISTER_USERS,
                variables: { page: 0, rowsPerPage, searchField, searchStr: searchInput, searchRegistered }
              },
            ]
          });
          const info = data.newRegisterUsers.registerUsers;
          if (info.err) {
            setErrorMsg(typeof info.msg === 'string' ? info.msg : JSON.stringify(info.msg, null, 2));
          } else {
            setReload((prev) => prev + 1);
            setPage(0);
            confirm({ 
              title: t('common.success'), 
              description: t('people.enroll_success'),
              cancellationText: t('common.cancel')
            });
          }
        } catch (error) {
          setErrorMsg(t('people.enroll_fail'));
          setOpenModal(true);
        }
      } else {
        setOpenModal(true);
      }
      setOpen(false);
    };
    fileReader.readAsArrayBuffer(file);
  };

  return (
    <Page title={t('nav.enroll')}>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={open}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Modal open={openModal} onClose={handleModalClose}>
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            {t('common.error_msg')}
          </Typography>
          <Box sx={{ overflow: 'visible', padding: 0, margin: 0 }}>
          <Typography
            id="modal-modal-description"
            sx={{
              mt: 2,
              whiteSpace: 'pre-wrap',
              width: '100%',
              display: 'block',
              maxWidth: '100%',
              maxHeight: '480px',
              overflowY: 'scroll',
              overflowX: 'hidden',
              paddingRight: '4px',
              paddingLeft: '4px',
              boxSizing: 'border-box',
              scrollbarWidth: 'thin',
              scrollbarColor: '#888 #f1f1f1',
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555',
              },            
            }}>
            {errorMsg}
          </Typography>
          </Box>
        </Box>
      </Modal>

      <Container maxWidth={false}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Stack direction="column">
            <Typography variant="h4" gutterBottom>
              {t('enroll.title')}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              component={RouterLink}
              to="/register/newuser"
              startIcon={<Iconify icon="eva:plus-fill" />}
            >
              {t('common.add_user')}
            </Button>
            <Button variant="contained" component="label" startIcon={<Iconify icon="eva:plus-fill" />}>
              {t('people.enroll_by_excel')}
              <input
                hidden
                accept=".xlsx, .xls, .csv"
                multiple
                type="file"
                onChange={handleUpload}
                onClick={(e) => { e.target.value = ''; }}
              />
            </Button>
            <Button variant="contained" component={RouterLink} startIcon={<FileDownloadIcon />} onClick={handleExport}>
              {t('common.export')}
            </Button>
            <Button
              variant="contained"
              component={RouterLink}
              startIcon={<FileDownloadIcon />}
              onClick={handleTemplate}
            >
              {t('common.template')}
            </Button>
          </Stack>
        </Stack>

        <Card>
          <ListToolbar
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            setSearchRegistered={setSearchRegistered}
            field={searchField}
            setSearchField={setSearchField}
            searchRegistered={searchRegistered}
            selection={REGISTER_USER.SEARCH_FIELDS.map(field => ({ id: field, label: t(`common.${field.toLowerCase()}`) }))}
          />

          <TablePagination
            rowsPerPageOptions={[15, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('common.rows_per_page')}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 0 }}>
              <Table>
                <ListHead headLabel={REGISTER_USER.TABLE_HEAD.map(head => ({ ...head, label: head.label ? t(`common.${head.label}`) : '' }))} />
                {searchInput || searchRegistered ? (
                  <SearchResultTable
                    searchInput={searchInput}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    setTotal={setTotal}
                    searchField={searchField}
                    searchRegistered={searchRegistered}
                    reload={reload}
                  />
                ) : (
                  <AllUserTable rowsPerPage={rowsPerPage} page={page} setTotal={setTotal} reload={reload} />
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Container>
    </Page>
  );
}
